import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { AuthUser, Permission } from "@brasilnovo/shared";
import { canAreaWith, canWriteEntityWith, hasPermission } from "@brasilnovo/shared";
import {
  acceptInvite as apiAcceptInvite, api,
  changePassword as apiChangePassword, login as apiLogin, logout as apiLogout,
  register as apiRegister, tokenStore,
} from "./api.js";

interface AuthCtx {
  user: AuthUser | null;
  login: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; name: string; password: string }) => Promise<void>;
  changePassword: (atual: string, nova: string) => Promise<void>;
  acceptInvite: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Senha provisionada por terceiro e ainda não trocada — o portal fica preso. */
  precisaTrocarSenha: boolean;
  canArea: (area: string) => boolean;
  canWrite: (entity: string) => boolean;
  /** Permissão nomeada (D-27). Sessão antiga sem lista → o papel decide. */
  can: (perm: Permission) => boolean;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(tokenStore.user);

  // Ao abrir o portal, relê o usuário: permissões mudadas pela administração
  // desde o último login aparecem sem precisar sair e entrar de novo.
  useEffect(() => {
    if (!tokenStore.access) return;
    api.get("/auth/me").then(({ data }) => {
      const { typ: _typ, iat: _iat, exp: _exp, ...fresh } = data as AuthUser & Record<string, unknown>;
      tokenStore.setUser(fresh as AuthUser);
      setUser(fresh as AuthUser);
    }).catch(() => { /* 401 já é tratado pelo interceptor */ });
  }, []);

  const value: AuthCtx = {
    user,
    login: async (email, password) => setUser(await apiLogin(email, password)),
    register: async (input) => setUser(await apiRegister(input)),
    changePassword: async (atual, nova) => setUser(await apiChangePassword(atual, nova)),
    acceptInvite: async (token, password) => setUser(await apiAcceptInvite(token, password)),
    logout: async () => {
      await apiLogout();
      setUser(null);
    },
    // `?? false` porque sessões gravadas antes desta versão não têm o campo:
    // ausente = não força troca a quem já estava logado.
    precisaTrocarSenha: user?.mustChangePassword ?? false,
    canArea: (area) => canAreaWith(user, area),
    canWrite: (entity) => canWriteEntityWith(user, entity),
    can: (perm) => hasPermission(user, perm),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth fora do AuthProvider");
  return c;
}
